import { useEffect, useMemo, useState } from "react";

import { useSelectedCarrier } from "~/features/quote/hooks/use-quote-hooks.ts";
import { getQuoteDetailsFingerprint } from "~/features/quote/utils/get-quote-details-fingerprint.ts";
import type { QuoteStateType } from "~/shared/types/quote-details-types.ts";

/**
 * Mirrors the quote-details `useStepLogic` change-detection for the two-step
 * `/ltl-shipment` flow. After a quote has been tendered (rates exist), it
 * captures a fingerprint of the shipment details. While the user is back on the
 * shipment-details step (step 1) and nothing has changed, the "Tender" button is
 * disabled (so no new quote ID is generated) and the carrier-selection step can
 * be revisited directly. Changing any shipment detail re-enables tendering and a
 * fresh quote ID is created on the next tender.
 *
 * `currentStep` is URL-derived in the LTL page (1 = shipment details,
 * 2 = carrier selection) and passed in so this hook stays decoupled from the
 * quote-route-based `useStepNavigation`.
 */
export const useLtlStepLogic = (
	quoteState: QuoteStateType,
	currentStep: number,
) => {
	const selectedCarrier = useSelectedCarrier(quoteState);

	const quoteDetailsFingerprint = useMemo(() => {
		return getQuoteDetailsFingerprint(quoteState);
	}, [quoteState]);

	const [quotedSnapshot, setQuotedSnapshot] = useState<{
		quoteId: number;
		fingerprint: string;
	} | null>(null);

	const hasQuotedRates =
		quoteState.id > 0 && quoteState.carrierPrices.length > 0;

	useEffect(() => {
		if (!hasQuotedRates) {
			if (quotedSnapshot !== null) {
				setQuotedSnapshot(null);
			}
			return;
		}

		const shouldCaptureInitialSnapshot =
			quotedSnapshot === null || quotedSnapshot.quoteId !== quoteState.id;
		const shouldRefreshFromCarrierSelection = currentStep > 1;

		if (!shouldCaptureInitialSnapshot && !shouldRefreshFromCarrierSelection) {
			return;
		}

		if (
			quotedSnapshot?.quoteId === quoteState.id &&
			quotedSnapshot.fingerprint === quoteDetailsFingerprint
		) {
			return;
		}

		setQuotedSnapshot({
			quoteId: quoteState.id,
			fingerprint: quoteDetailsFingerprint,
		});
	}, [
		hasQuotedRates,
		quotedSnapshot,
		quoteState.id,
		quoteDetailsFingerprint,
		currentStep,
	]);

	const quoteDetailsUnchanged =
		quotedSnapshot?.quoteId === quoteState.id &&
		quotedSnapshot.fingerprint === quoteDetailsFingerprint;
	const canRevisitCarrierSelection = hasQuotedRates && quoteDetailsUnchanged;
	const disableTenderButton =
		currentStep === 1 && hasQuotedRates && quoteDetailsUnchanged;

	return {
		selectedCarrier,
		canRevisitCarrierSelection,
		disableTenderButton,
	} as const;
};

export type LtlStepLogic = ReturnType<typeof useLtlStepLogic>;
